import {useEffect, useRef, useState} from "react";

import femaleImg from "@images/general/avatar_female.png";
import maleImg from "@images/general/avatar_male.png";
import {DOCTOR_ENUMS} from "@redux/slices/doctorSlices/createSlice";
import {
  clearViewDoctor,
  viewDoctor,
} from "@redux/slices/doctorSlices/viewSlice";
import {BackButton} from "@ui/buttons/BackButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {SmallDesc} from "@ui/descriptions/SmallDesc";
import {ViewField} from "@ui/forms/ViewField";
import {BorderArticle} from "@ui/general/BorderArticle";
import {Spinner} from "@ui/general/Spinner";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";

import styles from "./styles.module.scss";

const DoctorContentMainType = ({value}) => {
  const {t} = useTranslation();

  const [text, setText] = useState(value);

  useEffect(() => {
    switch (value) {
      case "analyst":
        setText(t("page.doctor.radio.main_type.analyst.desc"));
        break;
      case "ambitious":
        setText(t("page.doctor.radio.main_type.ambitious.desc"));
        break;
      case "caring":
        setText(t("page.doctor.radio.main_type.caring.desc"));
        break;
      case "simple":
        setText(t("page.doctor.radio.main_type.simple.desc"));
        break;
    }
  }, [value]);

  return (
    <div className={styles.doctorContentMainTypeDesc}>
      <BorderArticle>
        <SmallDesc>
          <span dangerouslySetInnerHTML={{__html: text}} />
        </SmallDesc>
      </BorderArticle>
    </div>
  );
};

export const DoctorViewPage = () => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {id} = useParams();

  const {isMobile} = useSelector((state) => state.common.settings);
  const {doctor, serverStatus} = useSelector((state) => state.doctor.view);

  const doctorRef = useRef();

  const genderOptions = [
    {
      label: (
        <div
          className={`${styles.doctorContentCover} ${
            doctor?.gender_type === "male"
              ? styles.doctorContentCoverActive
              : ""
          }`}
        >
          <img
            src={maleImg}
            alt={"male"}
          />

          <RootDesc>
            <b
              dangerouslySetInnerHTML={{__html: t("page.doctor.field.male")}}
            />
          </RootDesc>
        </div>
      ),
      value: "male",
    },
    {
      label: (
        <div
          className={`${styles.doctorContentCover} ${
            doctor?.gender_type === "female"
              ? styles.doctorContentCoverActive
              : ""
          }`}
        >
          <img
            src={femaleImg}
            alt={"female"}
          />

          <RootDesc>
            <b
              dangerouslySetInnerHTML={{
                __html: t("page.doctor.field.female"),
              }}
            />
          </RootDesc>
        </div>
      ),
      value: "female",
    },
  ];

  const mainTypeOptions = DOCTOR_ENUMS.main_type.map((value) => ({
    label: t(`page.doctor.radio.main_type.${value}.title`),
    value,
  }));

  const experienceOptions = DOCTOR_ENUMS.experience.map((value) => ({
    label: t(`page.doctor.radio.experience.${value}`),
    value,
  }));

  const medicalTypeOptions = DOCTOR_ENUMS.medical_type.map((type, id) => ({
    id,
    name: t(`page.doctor.radio.medical_type.${type}`),
    type,
  }));

  const prioritiesOptions = DOCTOR_ENUMS.priorities.map((value) => ({
    label: t(`page.doctor.radio.priorities.${value}`),
    value,
  }));

  const intensityOptions = DOCTOR_ENUMS.intensity.map((value) => ({
    label: t(`page.doctor.radio.intensity.${value}`),
    value,
  }));

  const activityOptions = DOCTOR_ENUMS.activity.map((value) => ({
    label: t(`page.doctor.radio.activity.${value}`),
    value,
  }));

  const specialityOptions = DOCTOR_ENUMS.speciality.map((value, id) => ({
    id,
    name: t(`page.doctor.radio.speciality.${value}`),
    value,
  }));

  useEffect(() => {
    dispatch(viewDoctor({id}));

    return () => {
      dispatch(clearViewDoctor());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (doctorRef.current && isMobile) {
      doctorRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [isMobile]);

  return (
    <div
      ref={doctorRef}
      className={styles.doctorWrapper}
    >
      <BorderArticle>
        <div className={styles.doctor}>
          <BackButton />

          {serverStatus === "loading" ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <div className={styles.doctorContent}>
              <div className={styles.doctorContentHeadWrapper}>
                <div className={styles.doctorContentHead}>
                  <div className={"flex items-center gap-[16rem]"}>
                    <h2>{doctor?.name}</h2>
                  </div>

                  <div className={"flex gap-[16rem]"}>
                    {
                      genderOptions.find(
                        (option) => option.value === doctor?.gender_type,
                      )?.label
                    }
                  </div>
                </div>
              </div>

              <ul className={styles.doctorContentBody}>
                <ViewField
                  label={t("page.doctor.field.main_type")}
                  customContent={
                    <div className={"flex flex-col gap-[16rem]"}>
                      <RootDesc>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: mainTypeOptions.find(
                              (option) => option.value === doctor?.main_type,
                            )?.label,
                          }}
                        />
                      </RootDesc>

                      <DoctorContentMainType value={doctor?.main_type} />
                    </div>
                  }
                />

                <ViewField
                  label={t("page.doctor.field.experience")}
                  value={
                    experienceOptions.find(
                      (option) => option.value === doctor?.experience,
                    )?.label ?? ""
                  }
                />

                <ViewField
                  label={t("page.doctor.field.medical_type")}
                  value={
                    medicalTypeOptions.find(
                      (option) => option.type === doctor?.medical_type,
                    )?.name ?? ""
                  }
                />

                <ViewField
                  label={t("page.doctor.field.priorities")}
                  value={
                    prioritiesOptions.find(
                      (option) => option.value === doctor?.priorities,
                    )?.label ?? ""
                  }
                />

                <ViewField
                  label={t("page.doctor.field.intensity")}
                  value={
                    intensityOptions.find(
                      (option) => option.value === doctor?.intensity,
                    )?.label ?? ""
                  }
                />

                <ViewField
                  label={t("page.doctor.field.activity")}
                  value={
                    activityOptions.find(
                      (option) => option.value === doctor?.activity,
                    )?.label ?? ""
                  }
                />

                {doctor?.drug_preference && (
                  <ViewField
                    label={t("page.doctor.field.drug_preference")}
                    value={t(
                      `page.doctor.radio.drug_preference.${doctor.drug_preference}`,
                    )}
                  />
                )}

                {doctor?.decision_making && (
                  <ViewField
                    label={t("page.doctor.field.decision_making")}
                    value={t(
                      `page.doctor.radio.decision_making.${doctor.decision_making}`,
                    )}
                  />
                )}

                <ViewField
                  label={t("page.doctor.field.speciality")}
                  value={
                    specialityOptions.find(
                      (option) => option.value === doctor?.speciality,
                    )?.name ?? ""
                  }
                />

                <ViewField
                  label={t("page.doctor.field.backstory")}
                  value={doctor?.backstory ?? ""}
                />
              </ul>
            </div>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
